import { memo } from 'react';
import { FileText, Pencil, User } from 'lucide-react';
import { MarkdownRenderer } from '../components/MarkdownRenderer/MarkdownRenderer';
import { ThinkingAccordion } from '../components/ThinkingAccordion/ThinkingAccordion';
import { Button } from '../components/atoms/Button';
import { MessageActions } from './MessageActions';
import { EditOperationsReview } from './EditOperationsReview';
import { cn } from '../lib/utils';
import type { ChatMessage, Citation } from './types';

interface Props {
  message: ChatMessage;
  /** Called when user clicks an inline citation marker (e.g. [db_1_cit3]) */
  onCitationClick?: (citationId: string) => void;
  /** Called when user clicks "Insert into document" on an answer or citation */
  onInsertIntoDocument?: (html: string, message: ChatMessage) => void;
}

/**
 * Single chat bubble. Mirrors the web app's MessageCard layout:
 *   - user message (right-aligned grey)
 *   - llm message (full width with streaming Thinking + markdown + citations)
 *   - change message (edit proposal summary with original/edited note)
 *   - info message (applied / rejected after accept)
 *
 * Wrapped in React.memo so typing into the input doesn't re-render every
 * message card (MarkdownRenderer re-parse is expensive on long threads).
 * Callers must stabilise the handler identity via useCallback — otherwise
 * memo's shallow equality check will break.
 */
function TaskPaneMessageCardImpl({
  message,
  onCitationClick,
  onInsertIntoDocument,
}: Props): JSX.Element {
  if (message.role === 'human') {
    return (
      <div className="flex justify-end mb-3">
        <div className="flex items-start gap-2 max-w-[90%]">
          <div className="rounded-2xl rounded-tr-sm bg-muted px-3 py-2 text-sm whitespace-pre-wrap break-words">
            {message.text}
          </div>
          <UserBadge />
        </div>
      </div>
    );
  }

  if (message.role === 'info') {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground italic">
        <Pencil className="w-3 h-3" />
        {message.text || (message.infoType === 'applied' ? 'Change applied' : 'Change rejected')}
      </div>
    );
  }

  // llm or change — both render with thinking/citations/text.
  // The `change` role means it was a quote_edit proposal applied as a tracked change.
  const isChange = message.role === 'change';

  // The NyayAssist avatar only appears once the assistant has actual content
  // to display — text, an error, an edit indicator, or the change-applied
  // chip. During the thinking-only phase the avatar stays hidden so the
  // thinking accordion floats on its own (matches frontend-prod behaviour
  // where the brand badge surfaces *with* the answer, not the thinking).
  // This rule applies to both research and edit modes — edit replies arrive
  // as `role='change'` (quote_edit) or `role='llm' + editedS3Key` (full_edit),
  // both of which set hasContent=true and surface the badge.
  const hasContent =
    !!message.text ||
    !!message.error ||
    !!message.editedS3Key ||
    isChange ||
    (message.citations && message.citations.length > 0);

  const body = (
    <>
      {message.thinking && message.thinking.length > 0 && (
        <ThinkingAccordion sections={message.thinking} streaming={!!message.streaming} />
      )}

      {isChange && (
        <div className="mb-2 rounded-md border border-border bg-muted/40 p-2 text-[11px] text-muted-foreground">
          <div className="font-medium text-foreground mb-0.5">Edit applied as tracked change</div>
          Review and accept/reject in Word&apos;s Review ribbon.
        </div>
      )}

      {message.text && (
        <>
          <MarkdownRenderer content={message.text} onCitationClick={onCitationClick} />
          {!message.streaming && !isChange && onInsertIntoDocument && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => onInsertIntoDocument(message.text, message)}
            >
              Insert answer into document
            </Button>
          )}
        </>
      )}

      {typeof message.editCount === 'number' && (
        <div className="mt-2 text-xs text-muted-foreground italic">
          {message.editCount} edit{message.editCount === 1 ? '' : 's'} applied as tracked changes.
        </div>
      )}

      {message.error && <div className="mt-2 text-xs text-red-600">Error: {message.error}</div>}

      {message.citations && message.citations.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-muted-foreground mb-2">Citations</div>
          <div className="space-y-1.5">
            {message.citations.map((c, i) => (
              <CitationRow
                key={c.id ?? i}
                index={i + 1}
                citation={c}
                onClick={() => onCitationClick?.(c.id)}
                onInsert={
                  onInsertIntoDocument && c.content
                    ? () => onInsertIntoDocument(c.content ?? '', message)
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-edit Accept/Reject cards — rendered for finished AI edits.
          The list is populated by EditOperationsReview itself enumerating
          the document's tracked changes directly (no AI-side metadata). */}
      {message.role === 'llm' &&
        !message.streaming &&
        !message.error &&
        !!message.editedS3Key && (
          <EditOperationsReview
            messageId={message.id}
            applyComplete={!message.streaming}
          />
        )}

      {/* Feedback / copy row — only for finished LLM answers (not edits, not errors) */}
      {message.role === 'llm' && !message.streaming && !message.error && message.text && (
        <MessageActions
          messageId={message.id}
          text={message.text}
          initialFeedback={message.feedback ?? null}
        />
      )}
    </>
  );

  // Thinking-only state: render the accordion full width, no avatar.
  // The avatar materialises once the first answer chunk lands and re-flows
  // the row into the badge + content layout.
  if (!hasContent) {
    return <div className="mb-4">{body}</div>;
  }

  return (
    <div className="mb-4 flex items-start gap-2">
      <AiBadge />
      <div className="flex-1 min-w-0">{body}</div>
    </div>
  );
}

function CitationRow({
  index,
  citation,
  onClick,
  onInsert,
}: {
  index: number;
  citation: Citation;
  onClick: () => void;
  onInsert?: () => void;
}): JSX.Element {
  const title = citation.title || citation.source || 'Source';
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-background p-2 text-xs">
      <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
        {index}
      </span>
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={onClick}
          className="text-left font-medium text-foreground hover:text-primary truncate block"
          title={title}
        >
          <FileText className="inline w-3 h-3 mr-1 -mt-0.5" />
          {title}
        </button>
        {citation.source && (
          <span className="text-[11px] text-muted-foreground">{citation.source}</span>
        )}
      </div>
      {onInsert && (
        <Button variant="ghost" size="sm" onClick={onInsert} className="h-6 text-[11px]">
          Insert
        </Button>
      )}
    </div>
  );
}

function UserBadge(): JSX.Element {
  return (
    <div
      className={cn(
        'flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center text-primary',
      )}
    >
      <User className="w-3.5 h-3.5" />
    </div>
  );
}

/**
 * NyayAssist branded avatar on AI message rows. Mirrors web's pattern of
 * surfacing the brand on every assistant reply. Sized to match UserBadge
 * (24px) so user/AI rows visually balance.
 */
function AiBadge(): JSX.Element {
  return (
    <div
      className={cn(
        'flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-white border border-primary/30 shadow-sm flex items-center justify-center overflow-hidden',
      )}
      aria-hidden
    >
      <img
        src="/logo_icon.png"
        alt="NyayAssist"
        width={16}
        height={16}
        style={{ width: 16, height: 16, objectFit: 'contain' }}
      />
    </div>
  );
}

export const TaskPaneMessageCard = memo(TaskPaneMessageCardImpl);
